import ComponentManager from "../../componentFramework";
import {default as JsfAjax, NativeJsfAjaxEvent, NativeJsfAjaxRequestOptions} from "../../scripts/jsf/JsfAjax";
import EventListenerManager from "../EventListenerManager";
import deepCopy, {deepCopyObject} from "../../scripts/deepCopy";
import eventListenerOption from "../../scripts/eventListenerOption";

/**
 * Used on h:commandLink (mostly) to hide the element. 
 * This class backs the element and allows us to do a few things easily.
 * 
 * Example use:
		<h:commandLink
			value="handlePageChange"
			styleClass="jsfJsCommand jsfJsCommand--action_handlePageChange"
			action="#{beanName.actionMethod}"
		><pf:ajax render="#{id}" /></h:commandLink>
 * Then in your TS code:
 		JsfJsCommand.run("handlePageChange", this.element, options);
 */
@ComponentManager.register()
export default class JsfJsCommand {
	public static componentStyleClass = "jsfJsCommand";
	public static readonly actionNameStyleClass = "jsfJsCommand--action_";
	private tempRequestOptions: NativeJsfAjaxRequestOptions | undefined;
	private tempInternalOptions: any | undefined;

	private readonly eventListenerManager = new EventListenerManager();

	/**
	 * Will return the instances of this JsfJsCommand component for the `actionName` provided.
	 * Since there can be multiple JsfJsCommand instances with the same `actionName` this returns
	 * an array. Use the second parameter, `containingElem` to limit the scope of the search within the DOM.
	 * If `containingElem` is not provided, the `document.documentElement` will be used.
	 * If no instances are found, this will return an empty array.
	 */
	public static getInstances(actionName: string, containingElem: Element = document.documentElement): JsfJsCommand[] {
		const componentInstances = new Array<JsfJsCommand>();

		const commandElements = containingElem.querySelectorAll<HTMLElement>("." + JsfJsCommand.actionNameStyleClass + actionName);
		if (commandElements.length === 0) {
			return componentInstances;
		}

		commandElements.forEach(commandElem => {
			const componentForElement = ComponentManager.getComponent(commandElem, JsfJsCommand);
			if (componentForElement !== undefined) {
				componentInstances.push(componentForElement);
			}
		});

		return componentInstances;
	}

	constructor(private readonly element: HTMLElement) {
		// Intercept "native" ajax requests
		this.eventListenerManager.manage(JsfAjax.getInstance())
			.addNativeJsfAjaxRequestListener(this.onAjaxRequest, this.element);

		// Even a call to ".click()" causes propagation so we need to stop it.
		this.eventListenerManager.manage(element).addEventListener(
			"click", ev => ev.stopPropagation(),
			eventListenerOption.passiveNotCapture
		);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
	}

	/**
	 * Finds all instances of JsfJsCommand for the `actionName` and runs them.
	 * You can use the second argument, `containingElement`, to limit the scope of the DOM to search for the actionName.
	 *
	 * The `requestOptions` and `containingElement` arguments are requestOptions and may be excluded or set to null or undefined.
	 * Returns `true` if an instance of JsfJsCommand was found and ran.
	 */
	public static run(actionName: string, containingElement?: Element, requestOptions?: NativeJsfAjaxRequestOptions, requestInternalOptions?: any): boolean {
		const commandInstances = JsfJsCommand.getInstances(actionName, containingElement ?? document.documentElement);
		if (commandInstances.length === 0) {
			return false;
		}
		commandInstances.forEach(commandInstance => {
			commandInstance.run(requestOptions, requestInternalOptions);
		});
		return true;
	}

	public run(requestOptions?: NativeJsfAjaxRequestOptions, requestInternalOptions?: any) {
		if (this.tempRequestOptions !== undefined && this.tempInternalOptions !== undefined) {
			// TODO @Michael: What should we do about this? Should we implement some waiting which is triggered once onJaxRequest is ran? A simple queue should do the trick
			throw new Error("JsfJsCommand was triggered to run while it is already running.");
		}
		if (requestOptions !== undefined && requestOptions !== null) {
			this.tempRequestOptions = deepCopyObject(requestOptions);
		}
		if (requestInternalOptions !== undefined && requestInternalOptions !== null) {
			this.tempInternalOptions = deepCopyObject(requestInternalOptions);
		}
		this.element.click(); // Causes JSF to do its thing
	}

	private readonly onAjaxRequest = (sourceElement: Element, event: Event, requestOptions: NativeJsfAjaxRequestOptions, internalOptions: any) => {
		// If the request is from this JsfJsCommand instance
		if (sourceElement === this.element) {

			// If run() was passed requestOptions (now called) tempRequestOptions,  copy the data to the
			// requestOptions on the actual request (the argument passed to this method).
			// We have to deal with onevent and onerror properties to ensure the copy form both options are ran.
			if (this.tempRequestOptions !== undefined) {
				const onMethodNames = ["onevent", "onerror"];
				for (let i = 0; i < onMethodNames.length; i++) {
					let methodName = onMethodNames[i]!;
					if (this.tempRequestOptions[methodName] !== undefined && requestOptions[methodName] !== undefined) {
						const tempOnEvent = this.tempRequestOptions[methodName];
						const optionsOnEvent = requestOptions[methodName];
						this.tempRequestOptions[methodName] = function (ev: NativeJsfAjaxEvent) {
							tempOnEvent.call(this, ev);
							optionsOnEvent.call(this, ev);
						}
					}
				}
				deepCopy([this.tempRequestOptions], {target: requestOptions});
			}

			// If run() was passed requestInternalOptions (now called) tempInternalOptions,  copy the data to the
			// internalOptions on the actual request (the argument passed to this method).
			if (this.tempInternalOptions !== undefined) {
				deepCopy([this.tempInternalOptions], {target: internalOptions});
			}

			// Set both temp holders to undefined
			this.tempRequestOptions = undefined;
			this.tempInternalOptions = undefined;
		}
	}
}
